export const PROGRAM_SCOPED_LOCALE_QUERY_PARAM = "programLocale";
export const PROGRAM_SCOPED_LOCALE_STORAGE_PREFIX =
  "all_voiced_program_locale";

export const PROGRAM_SCOPED_LOCALE_OPTIONS = Object.freeze({
  en: Object.freeze({
    locale: "en",
    label: "English",
    labelKey: "program_language_option_en"
  }),
  "fr-CA": Object.freeze({
    locale: "fr-CA",
    label: "Français (Canada)",
    labelKey: "program_language_option_fr_ca"
  })
});

const PROGRAM_SCOPED_LOCALE_CONFIGS = Object.freeze([
  Object.freeze({
    id: "studiumfc-montreal",
    programSlug: "studiumfc-program",
    projectSlugs: Object.freeze([
      "studiumfc-ce-project",
      "studiumfc-master-project"
    ]),
    supportedLocales: Object.freeze(["en", "fr-CA"]),
    defaultLocale: "en"
  })
]);

export function normalizeProgramScopedLocale(locale) {
  if (!locale || typeof locale !== "string") {
    return null;
  }

  const normalized = locale.trim().replace("_", "-").toLowerCase();

  if (normalized === "en" || normalized === "en-us") {
    return "en";
  }

  if (normalized === "fr-ca") {
    return "fr-CA";
  }

  return null;
}

export function getProgramScopedLocaleConfig({
  projectSlug,
  programSlug
} = {}) {
  if (!projectSlug || !programSlug) {
    return null;
  }

  const matchedConfig = PROGRAM_SCOPED_LOCALE_CONFIGS.find((config) => (
    config.programSlug === programSlug &&
    config.projectSlugs.includes(projectSlug)
  ));

  return matchedConfig
    ? Object.freeze({
      ...matchedConfig,
      projectSlug
    })
    : null;
}

export function getProgramScopedLocaleConfigForCatalogEntry(entry) {
  const projectSlug = entry?.source?.type === "project"
    ? entry.source.slug
    : null;

  if (!projectSlug || entry?.primaryType !== "program") {
    return null;
  }

  const matchedConfig = PROGRAM_SCOPED_LOCALE_CONFIGS.find((config) =>
    config.projectSlugs.includes(projectSlug)
  );

  return matchedConfig
    ? Object.freeze({
      ...matchedConfig,
      projectSlug
    })
    : null;
}

export function isProgramScopedLocaleSupported(config, locale) {
  const normalizedLocale = normalizeProgramScopedLocale(locale);

  return Boolean(
    config &&
    normalizedLocale &&
    config.supportedLocales.includes(normalizedLocale)
  );
}

export function getProgramScopedLocaleStorageKey(config) {
  if (!config) {
    return null;
  }

  return [
    PROGRAM_SCOPED_LOCALE_STORAGE_PREFIX,
    config.projectSlug,
    config.programSlug
  ].join(":");
}

export function getProgramScopedLocaleOption(locale) {
  const normalizedLocale = normalizeProgramScopedLocale(locale);

  return normalizedLocale
    ? PROGRAM_SCOPED_LOCALE_OPTIONS[normalizedLocale] || null
    : null;
}

export function getProgramScopedLocaleOptions(config) {
  if (!config) {
    return [];
  }

  return config.supportedLocales
    .map(getProgramScopedLocaleOption)
    .filter(Boolean);
}

export function resolveProgramScopedLocale({
  config,
  urlLocale,
  storedLocale,
  userCommunicationLanguage
} = {}) {
  if (!config) {
    return "en";
  }

  const candidates = [
    urlLocale,
    storedLocale,
    userCommunicationLanguage,
    config.defaultLocale
  ];

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeProgramScopedLocale(candidate);

    if (
      normalizedCandidate &&
      config.supportedLocales.includes(normalizedCandidate)
    ) {
      return normalizedCandidate;
    }
  }

  return config.defaultLocale || "en";
}

export function hasValidProgramScopedLocaleUrlValue(config, urlLocale) {
  return isProgramScopedLocaleSupported(config, urlLocale);
}
